/**
 * Schedule Overview Tab
 *
 * Displays schedule metadata and provides edit/delete/publish functionality
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
  useRunScheduleMutation,
} from '../../../api/schedulingApi';
import type { Schedule, ScheduleTab } from '../../../types/scheduling';
import ScheduleVisualizationDialog from '../components/ScheduleVisualizationDialog';
import ScheduleExcelDialog from '../components/ScheduleExcelDialog';
import DuplicateScheduleDialog from '../components/DuplicateScheduleDialog';

interface ScheduleOverviewTabProps {
  schedule: Schedule;
  onTabChange?: (tab: ScheduleTab) => void;
}

const ScheduleOverviewTab: React.FC<ScheduleOverviewTabProps> = ({
  schedule,
  onTabChange,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(schedule.name);
  const [description, setDescription] = useState(schedule.description || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [visualizationDialogOpen, setVisualizationDialogOpen] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  const [updateSchedule, { isLoading: isUpdating }] =
    useUpdateScheduleMutation();
  const [deleteSchedule, { isLoading: isDeleting }] =
    useDeleteScheduleMutation();
  const [runSchedule, { isLoading: isRunning }] = useRunScheduleMutation();

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setName(schedule.name);
    setDescription(schedule.description || '');
  };

  const handleSave = async () => {
    try {
      await updateSchedule({
        id: schedule.id,
        studioId: schedule.studio,
        data: { name, description },
      }).unwrap();
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update schedule:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSchedule({
        id: schedule.id,
        studioId: schedule.studio,
      }).unwrap();
      navigate('/scheduling');
    } catch (error) {
      console.error('Failed to delete schedule:', error);
    }
  };

  const handleRunSchedule = async () => {
    try {
      await runSchedule({
        id: schedule.id,
        studioId: schedule.studio,
      }).unwrap();
      // Switch to Algorithm Results tab on success
      if (onTabChange) {
        onTabChange('results');
      }
    } catch (error) {
      console.error('Failed to run schedule:', error);
      // Error handling - the results tab will show the error
      // Still switch to results tab so user can see what went wrong
      if (onTabChange) {
        onTabChange('results');
      }
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      {/* Edit Form */}
      {isEditing ? (
        <Box component="form" noValidate autoComplete="off">
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Schedule Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              multiline
              rows={3}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isUpdating || !name.trim()}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={3}>
          {/* Status and Metadata */}
          {schedule.is_active && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip label="Active" color="primary" />
            </Stack>
          )}

          {/* Metadata */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">
                {formatDateTime(schedule.created_at)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {formatDateTime(schedule.updated_at)}
              </Typography>
            </Box>
          </Box>

          {/* Counts */}
          <Alert severity="info">
            This schedule has {schedule.class_count} classes,{' '}
            {schedule.room_count} rooms, and {schedule.teacher_count} teachers
            configured.
          </Alert>

          {/* Actions */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit Details
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={
                isRunning ? <CircularProgress size={20} /> : <PlayArrowIcon />
              }
              onClick={handleRunSchedule}
              disabled={isRunning}
            >
              {isRunning ? 'Running Algorithm...' : 'Run Schedule'}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<TableChartIcon />}
              onClick={() => setExcelDialogOpen(true)}
            >
              Manage Excel Data
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ImageIcon />}
              onClick={() => setVisualizationDialogOpen(true)}
            >
              Download Schedule Image
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<ContentCopyIcon />}
              onClick={() => setDuplicateDialogOpen(true)}
            >
              Duplicate Schedule
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Schedule
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Schedule?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{schedule.name}&quot;? This
            action cannot be undone. All classes, rooms, teachers, and
            scheduling results associated with this schedule will be permanently
            deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Data Management Dialog */}
      <ScheduleExcelDialog
        open={excelDialogOpen}
        onClose={() => setExcelDialogOpen(false)}
        scheduleId={schedule.id}
        studioId={schedule.studio}
        scheduleName={schedule.name}
        classCount={schedule.class_count}
        roomCount={schedule.room_count}
        teacherCount={schedule.teacher_count}
        onUploadSuccess={() => {
          // Refetch schedule data to update counts
          window.location.reload();
        }}
      />

      {/* Visualization Dialog */}
      <ScheduleVisualizationDialog
        open={visualizationDialogOpen}
        onClose={() => setVisualizationDialogOpen(false)}
        scheduleId={schedule.id}
        studioId={schedule.studio}
        scheduleName={schedule.name}
      />

      {/* Duplicate Schedule Dialog */}
      <DuplicateScheduleDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        scheduleId={schedule.id}
        scheduleName={schedule.name}
        studioId={schedule.studio}
        onSuccess={newScheduleId => {
          // Navigate to the newly duplicated schedule
          navigate(`/scheduling/${newScheduleId}`);
        }}
      />
    </Box>
  );
};

export default ScheduleOverviewTab;
